import React, { useEffect, useState } from 'react';
import {XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Bar, BarChart } from 'recharts';
import MultiSelectDropdown from '../multiselect/MultiSelectDropdown';
import { useProductionData } from '../../contexts/ProductionDataContext';
import { fetchProductionReport } from '../../services/api';
import { idsMatch } from '../../Utils/objectIdMatch';

const colors = ["rgb(156,39,176)", "rgb(255,152,0)", "rgb(76,175,80)", "#ff7300", "#ff0000", "#0000ff"]; 
const StopeChart = ({stopeID}) => {
  const [data, setData] = useState([]);
  const [alldata, setAllData] = useState({});

  
  const [stopeData, setStopeData] = useState([[],'Month',[]]);

  const [summary_field, setSummaryField] = useState('Month');

  const [parsedData, setParsedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState('FY2025'); // Dynamic fiscal year
  
  // Get production data from context
  const { loading: dataLoading } = useProductionData();
  
  // Fetch data from production.php API endpoint
  useEffect(() => {
    const fetchDatabaseData = async () => {
      setLoading(true);
      try {
        // Fetch aggregated production data from production.php API
        const [stopingReport, trammingReport] = await Promise.all([
          fetchProductionReport('stoping'),
          fetchProductionReport('tramming')
        ]);
        
        if (!stopingReport.success || !trammingReport.success) {
          console.error('Error fetching production reports:', {
            stoping: stopingReport.error,
            tramming: trammingReport.error
          });
          setParsedData([]);
          return;
        }
        
        // Transform production report data to match CSV structure
        const transformedData = transformProductionReportData(
          stopingReport.data,
          trammingReport.data
        );
        
        // Extract fiscal year(s) dynamically from the data
        if (transformedData && transformedData.length > 0) {
          const years = new Set();
          transformedData.forEach(row => {
            if (row.Date) {
              const parts = row.Date.split('/');
              if (parts.length === 3) {
                const year = parseInt(parts[2]);
                if (!isNaN(year)) {
                  years.add(year);
                }
              }
            }
          });
          
          // Use the latest year as the fiscal year, or default to current year
          const sortedYears = Array.from(years).sort((a, b) => b - a);
          const latestYear = sortedYears.length > 0 ? sortedYears[0] : new Date().getFullYear();
          setFiscalYear(`FY${latestYear}`);
        }
        
        setParsedData(transformedData);
      } catch (error) {
        console.error('Error fetching database data:', error);
        setParsedData([]); // Set empty array if database fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchDatabaseData();
  }, []);
  
  // Transform production report data to match CSV structure
  const transformProductionReportData = (stopingReport, trammingReport) => {
    const dataMap = new Map(); // Key: stope_id + date
    
    // Helper to get date string in DD/MM/YYYY format
    const getDateStr = (dateStr) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };
    
    // Helper to extract level from stope_id (e.g., "1L" from "1L_STOPE01")
    const getLevelFromStopeId = (stopeId) => {
      if (!stopeId) return '';
      const match = stopeId.match(/^(\d+L)/);
      return match ? match[1] : '';
    };
    
    // Process stoping report tracker data (per-stope, per-date breakdown)
    if (stopingReport && stopingReport.tracker && stopingReport.tracker.entities) {
      stopingReport.tracker.entities.forEach(entity => {
        const stopeId = entity.entityId || '';
        const level = getLevelFromStopeId(stopeId);
        
        // Process each day's data for this stope
        if (stopingReport.tracker.days && entity.rows) {
          stopingReport.tracker.days.forEach(day => {
            const dateStr = getDateStr(day);
            if (!dateStr) return;
            
            const key = `${stopeId}_${dateStr}`;
            
            // Get actual values (metricType === 'Actual')
            const actualTonnageRow = entity.rows.find(r => r.metricKey === 'tonnage' && r.metricType === 'Actual');
            const actualGradeRow = entity.rows.find(r => r.metricKey === 'grade' && r.metricType === 'Actual');
            const actualContentRow = entity.rows.find(r => r.metricKey === 'content' && r.metricType === 'Actual');
            
            // Get budget values (metricType === 'Budget')
            const budgetTonnageRow = entity.rows.find(r => r.metricKey === 'tonnage' && r.metricType === 'Budget');
            const budgetGradeRow = entity.rows.find(r => r.metricKey === 'grade' && r.metricType === 'Budget');
            const budgetContentRow = entity.rows.find(r => r.metricKey === 'content' && r.metricType === 'Budget');
            
            const actualTonnage = actualTonnageRow?.values?.[day] || 0;
            const actualGrade = actualGradeRow?.values?.[day] || 0;
            const actualContent = actualContentRow?.values?.[day] || 0;
            const budgetTonnage = budgetTonnageRow?.values?.[day] || 0;
            const budgetGrade = budgetGradeRow?.values?.[day] || 0;
            const budgetContent = budgetContentRow?.values?.[day] || 0;
            
            if (actualTonnage > 0 || budgetTonnage > 0) {
              if (!dataMap.has(key)) {
                dataMap.set(key, {
                  ID: stopeId,
                  Date: dateStr,
                  Level: level,
                  // Dynamic fiscal year fields will be added based on data
                  Plan_t: 0,
                  Plan_gpt: 0,
                  Plan_kgs: 0,
                  Stoping_Budget_t: 0,
                  Stoping_Budget_gpt: 0,
                  Stoping_Budget_kgs: 0,
                  Stoping_Actual_t: 0,
                  Stoping_Actual_gpt: 0,
                  Stoping_Actual_kgs: 0,
                  Tramming_Budget_t: 0,
                  Tramming_Budget_gpt: 0,
                  Tramming_Budget_kgs: 0,
                  Tramming_Actual_t: 0,
                  Tramming_Actual_gpt: 0,
                  Tramming_Actual_kgs: 0
                });
              }
              
              const record = dataMap.get(key);
              record.Stoping_Actual_t += actualTonnage;
              record.Stoping_Actual_gpt = actualGrade || record.Stoping_Actual_gpt || 0;
              record.Stoping_Actual_kgs += actualContent;
              
              record.Stoping_Budget_t += budgetTonnage;
              record.Stoping_Budget_gpt = budgetGrade || record.Stoping_Budget_gpt || 0;
              record.Stoping_Budget_kgs += budgetContent;
            }
          });
        }
      });
    }
    
    // Process tramming report tracker data
    if (trammingReport && trammingReport.tracker && trammingReport.tracker.entities) {
      trammingReport.tracker.entities.forEach(entity => {
        if (trammingReport.tracker.days && entity.rows) {
          trammingReport.tracker.days.forEach(day => {
            const dateStr = getDateStr(day);
            if (!dateStr) return;
            
            // Get actual and budget rows
            const actualTonnageRow = entity.rows.find(r => r.metricKey === 'tonnage' && r.metricType === 'Actual');
            const actualGradeRow = entity.rows.find(r => r.metricKey === 'grade' && r.metricType === 'Actual');
            const actualContentRow = entity.rows.find(r => r.metricKey === 'content' && r.metricType === 'Actual');
            
            const budgetTonnageRow = entity.rows.find(r => r.metricKey === 'tonnage' && r.metricType === 'Budget');
            const budgetGradeRow = entity.rows.find(r => r.metricKey === 'grade' && r.metricType === 'Budget');
            const budgetContentRow = entity.rows.find(r => r.metricKey === 'content' && r.metricType === 'Budget');
            
            const actualTonnage = actualTonnageRow?.values?.[day] || 0;
            const actualGrade = actualGradeRow?.values?.[day] || 0;
            const actualContent = actualContentRow?.values?.[day] || 0;
            const budgetTonnage = budgetTonnageRow?.values?.[day] || 0;
            const budgetGrade = budgetGradeRow?.values?.[day] || 0;
            const budgetContent = budgetContentRow?.values?.[day] || 0;
            
            // For tramming, aggregate by date (not by entity)
            const key = `TRAMMING_${dateStr}`;
            
            if (!dataMap.has(key)) {
              dataMap.set(key, {
                ID: '',
                Date: dateStr,
                Level: '',
                // Dynamic fiscal year fields will be added based on data
                Plan_t: 0,
                Plan_gpt: 0,
                Plan_kgs: 0,
                Stoping_Budget_t: 0,
                Stoping_Budget_gpt: 0,
                Stoping_Budget_kgs: 0,
                Stoping_Actual_t: 0,
                Stoping_Actual_gpt: 0,
                Stoping_Actual_kgs: 0,
                Tramming_Budget_t: 0,
                Tramming_Budget_gpt: 0,
                Tramming_Budget_kgs: 0,
                Tramming_Actual_t: 0,
                Tramming_Actual_gpt: 0,
                Tramming_Actual_kgs: 0
              });
            }
            
            const record = dataMap.get(key);
            record.Tramming_Actual_t += actualTonnage;
            record.Tramming_Actual_gpt = actualGrade || record.Tramming_Actual_gpt || 0;
            record.Tramming_Actual_kgs += actualContent;
            
            record.Tramming_Budget_t += budgetTonnage;
            record.Tramming_Budget_gpt = budgetGrade || record.Tramming_Budget_gpt || 0;
            record.Tramming_Budget_kgs += budgetContent;
          });
        }
      });
    }
    
    // Calculate grades from content for records that don't have grade set
    // Keep per-stope per-date records for stoping, aggregate tramming by date
    const trammingByDate = new Map();
    
    dataMap.forEach((record, key) => {
      // Calculate grades if missing
      if (record.Stoping_Budget_t > 0 && record.Stoping_Budget_kgs > 0 && !record.Stoping_Budget_gpt) {
        record.Stoping_Budget_gpt = (record.Stoping_Budget_kgs * 1000) / record.Stoping_Budget_t;
      }
      if (record.Stoping_Actual_t > 0 && record.Stoping_Actual_kgs > 0 && !record.Stoping_Actual_gpt) {
        record.Stoping_Actual_gpt = (record.Stoping_Actual_kgs * 1000) / record.Stoping_Actual_t;
      }
      if (record.Tramming_Budget_t > 0 && record.Tramming_Budget_kgs > 0 && !record.Tramming_Budget_gpt) {
        record.Tramming_Budget_gpt = (record.Tramming_Budget_kgs * 1000) / record.Tramming_Budget_t;
      }
      if (record.Tramming_Actual_t > 0 && record.Tramming_Actual_kgs > 0 && !record.Tramming_Actual_gpt) {
        record.Tramming_Actual_gpt = (record.Tramming_Actual_kgs * 1000) / record.Tramming_Actual_t;
      }
      
      // Aggregate tramming data by date (since tramming doesn't have stope_id)
      if (key.startsWith('TRAMMING_')) {
        const dateStr = record.Date;
        if (!trammingByDate.has(dateStr)) {
          trammingByDate.set(dateStr, {
            Tramming_Budget_t: 0,
            Tramming_Budget_gpt: 0,
            Tramming_Budget_kgs: 0,
            Tramming_Actual_t: 0,
            Tramming_Actual_gpt: 0,
            Tramming_Actual_kgs: 0
          });
        }
        const tramming = trammingByDate.get(dateStr);
        tramming.Tramming_Budget_t += record.Tramming_Budget_t;
        tramming.Tramming_Budget_kgs += record.Tramming_Budget_kgs;
        tramming.Tramming_Actual_t += record.Tramming_Actual_t;
        tramming.Tramming_Actual_kgs += record.Tramming_Actual_kgs;
      }
    });
    
    // Remove tramming-only records and merge tramming data into stoping records by date
    const stopingRecords = Array.from(dataMap.entries()).filter(([key]) => !key.startsWith('TRAMMING_'));
    const result = [];
    
    stopingRecords.forEach(([key, record]) => {
      const dateStr = record.Date;
      const tramming = trammingByDate.get(dateStr);
      
      if (tramming) {
        // Add tramming data to this stoping record
        record.Tramming_Budget_t = tramming.Tramming_Budget_t;
        record.Tramming_Budget_kgs = tramming.Tramming_Budget_kgs;
        record.Tramming_Actual_t = tramming.Tramming_Actual_t;
        record.Tramming_Actual_kgs = tramming.Tramming_Actual_kgs;
        
        // Calculate grades
        if (tramming.Tramming_Budget_t > 0 && tramming.Tramming_Budget_kgs > 0) {
          record.Tramming_Budget_gpt = (tramming.Tramming_Budget_kgs * 1000) / tramming.Tramming_Budget_t;
        }
        if (tramming.Tramming_Actual_t > 0 && tramming.Tramming_Actual_kgs > 0) {
          record.Tramming_Actual_gpt = (tramming.Tramming_Actual_kgs * 1000) / tramming.Tramming_Actual_t;
        }
      }
      
      result.push(record);
    });
    
    return result;
  };
  
  

  // Process data when parsedData, stopeID, or filters change
  useEffect(() => {     
    if(!parsedData || loading || dataLoading) return

      console.log(parsedData)
      let filteredData = parsedData;
      if (stopeID) {
        filteredData = parsedData.filter((row) => idsMatch(row['ID'], stopeID));
      }

      if(stopeData[0].length>0){
        filteredData = filteredData.filter(row => stopeData[0].includes(row['Level']));
      }

      if(stopeData[2].length>0){
        filteredData = filteredData.filter(row => stopeData[2].includes(row[stopeData[1]]));
      }

      console.log(filteredData)

      // Add Month, Quarter, Year fields to each row like in BottomBar
      const processedData = filteredData.map(row => {
        const dateStr = row.Date;
        let date = null;
        
        if (dateStr) {
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const year = parseInt(parts[2]);
            date = new Date(year, month, day);
          }
        }

        return {
          ...row,
          Month: date ? date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear() : '',
          Quarter: date ? `Q${Math.floor((date.getMonth() + 3) / 3)} ${date.getFullYear()}` : '',
          Year: date ? date.getFullYear().toString() : ''
        };
      });

      // Create dynamic field names based on fiscal year
      const planTonnageKey = `${fiscalYear}_Plan_Tonnage`;
      const planGradeKey = `${fiscalYear}_Plan_Grade`;
      const planContentKey = `${fiscalYear}_Plan_Content`;
      const planTonnageSumKey = `${fiscalYear}_Plan_Tonnage_Sum`;
      
      const summary = {};
      const totalSummary = {
        [planTonnageKey]: 0,
        Planned_Tonnage: 0,
        Mined_Tonnage: 0,
        Tramming_Budget_Tonnage: 0,
        Tramming_Actual_Tonnage: 0,
        [planGradeKey]: 0,
        Planned_Grade: 0,
        Actual_Grade: 0,
        Tramming_Budget_Grade: 0,
        Tramming_Actual_Grade: 0,
        [planContentKey]: 0,
        Planned_Content: 0,
        Actual_Content: 0,
        Tramming_Budget_Content: 0,
        Tramming_Actual_Content: 0,
        [planTonnageSumKey]: 0,
        Planned_Tonnage_Sum: 0,
        Mined_Tonnage_Sum: 0,
        Tramming_Budget_Tonnage_Sum: 0,
        Tramming_Actual_Tonnage_Sum: 0
      };

      processedData.forEach(row => {
        const key = row[summary_field];
        if (!summary[key]) {
          summary[key] = {
            [planTonnageKey]: 0,
            Planned_Tonnage: 0,
            Mined_Tonnage: 0,
            Tramming_Budget_Tonnage: 0,
            Tramming_Actual_Tonnage: 0,
            [planGradeKey]: 0,
            Planned_Grade: 0,
            Actual_Grade: 0,
            Tramming_Budget_Grade: 0,
            Tramming_Actual_Grade: 0,
            [planContentKey]: 0,
            Planned_Content: 0,
            Actual_Content: 0,
            Tramming_Budget_Content: 0,
            Tramming_Actual_Content: 0,
            [planTonnageSumKey]: 0,
            Planned_Tonnage_Sum: 0,
            Mined_Tonnage_Sum: 0,
            Tramming_Budget_Tonnage_Sum: 0,
            Tramming_Actual_Tonnage_Sum: 0
          };
        }
        
        // Dynamic Plan data - try to find fiscal year fields in row
        const planTonnageField = Object.keys(row).find(k => k.includes('Breaking_Plan_t') || k.includes('Plan_t'));
        const planGradeField = Object.keys(row).find(k => k.includes('Breaking_Plan_gpt') || k.includes('Plan_gpt'));
        const planKgsField = Object.keys(row).find(k => k.includes('Breaking_Plan_kgs') || k.includes('Plan_kgs'));
        
        const planTonnage = parseFloat(row[planTonnageField] || row['FY2025_Breaking_Plan_t'] || 0);
        const planGrade = parseFloat(row[planGradeField] || row['FY2025_Breaking_Plan_gpt'] || 0);
        const planKgs = parseFloat(row[planKgsField] || row['FY2025_Breaking_Plan_kgs'] || 0);
        
        // Budget (Planned) data
        const plannedTonnage = parseFloat(row['Stoping_Budget_t']) || 0;
        const plannedGrade = parseFloat(row['Stoping_Budget_gpt']) || 0;
        const plannedKgs = parseFloat(row['Stoping_Budget_kgs']) || 0;
        
        // Actual (Mined) data
        const actualTonnage = parseFloat(row['Stoping_Actual_t']) || 0;
        const actualGrade = parseFloat(row['Stoping_Actual_gpt']) || 0;
        const actualKgs = parseFloat(row['Stoping_Actual_kgs']) || 0;
        
        // Tramming Budget data
        const trammingBudgetTonnage = parseFloat(row['Tramming_Budget_t']) || 0;
        const trammingBudgetGrade = parseFloat(row['Tramming_Budget_gpt']) || 0;
        const trammingBudgetKgs = parseFloat(row['Tramming_Budget_kgs']) || 0;
        
        // Tramming Actual data
        const trammingActualTonnage = parseFloat(row['Tramming_Actual_t']) || 0;
        const trammingActualGrade = parseFloat(row['Tramming_Actual_gpt']) || 0;
        const trammingActualKgs = parseFloat(row['Tramming_Actual_kgs']) || 0;

        // Accumulate Plan data
        summary[key][planTonnageKey] += planTonnage;
        summary[key][planGradeKey] += planGrade * planTonnage;
        summary[key][planContentKey] += planKgs;
        summary[key][planTonnageSumKey] += planTonnage;

        // Accumulate Budget (Planned) data
        summary[key].Planned_Tonnage += plannedTonnage;
        summary[key].Planned_Grade += plannedGrade * plannedTonnage;
        summary[key].Planned_Content += plannedKgs;
        summary[key].Planned_Tonnage_Sum += plannedTonnage;

        // Accumulate Actual (Mined) data
        summary[key].Mined_Tonnage += actualTonnage;
        summary[key].Actual_Grade += actualGrade * actualTonnage;
        summary[key].Actual_Content += actualKgs;
        summary[key].Mined_Tonnage_Sum += actualTonnage;

        // Accumulate Tramming Budget data
        summary[key].Tramming_Budget_Tonnage += trammingBudgetTonnage;
        summary[key].Tramming_Budget_Grade += trammingBudgetGrade * trammingBudgetTonnage;
        summary[key].Tramming_Budget_Content += trammingBudgetKgs;
        summary[key].Tramming_Budget_Tonnage_Sum += trammingBudgetTonnage;

        // Accumulate Tramming Actual data
        summary[key].Tramming_Actual_Tonnage += trammingActualTonnage;
        summary[key].Tramming_Actual_Grade += trammingActualGrade * trammingActualTonnage;
        summary[key].Tramming_Actual_Content += trammingActualKgs;
        summary[key].Tramming_Actual_Tonnage_Sum += trammingActualTonnage;

        // Total summaries
        totalSummary[planTonnageKey] += planTonnage;
        totalSummary[planGradeKey] += planGrade * planTonnage;
        totalSummary[planContentKey] += planKgs;
        totalSummary[planTonnageSumKey] += planTonnage;

        totalSummary.Planned_Tonnage += plannedTonnage;
        totalSummary.Planned_Grade += plannedGrade * plannedTonnage;
        totalSummary.Planned_Content += plannedKgs;
        totalSummary.Planned_Tonnage_Sum += plannedTonnage;

        totalSummary.Mined_Tonnage += actualTonnage;
        totalSummary.Actual_Grade += actualGrade * actualTonnage;
        totalSummary.Actual_Content += actualKgs;
        totalSummary.Mined_Tonnage_Sum += actualTonnage;

        totalSummary.Tramming_Budget_Tonnage += trammingBudgetTonnage;
        totalSummary.Tramming_Budget_Grade += trammingBudgetGrade * trammingBudgetTonnage;
        totalSummary.Tramming_Budget_Content += trammingBudgetKgs;
        totalSummary.Tramming_Budget_Tonnage_Sum += trammingBudgetTonnage;

        totalSummary.Tramming_Actual_Tonnage += trammingActualTonnage;
        totalSummary.Tramming_Actual_Grade += trammingActualGrade * trammingActualTonnage;
        totalSummary.Tramming_Actual_Content += trammingActualKgs;
        totalSummary.Tramming_Actual_Tonnage_Sum += trammingActualTonnage;
      });

      // Calculate weighted averages
      totalSummary[planGradeKey] = totalSummary[planTonnageSumKey] > 0 ? totalSummary[planGradeKey] / totalSummary[planTonnageSumKey] : 0;
      totalSummary.Planned_Grade = totalSummary.Planned_Tonnage_Sum > 0 ? totalSummary.Planned_Grade / totalSummary.Planned_Tonnage_Sum : 0;
      totalSummary.Actual_Grade = totalSummary.Mined_Tonnage_Sum > 0 ? totalSummary.Actual_Grade / totalSummary.Mined_Tonnage_Sum : 0;
      totalSummary.Tramming_Budget_Grade = totalSummary.Tramming_Budget_Tonnage_Sum > 0 ? totalSummary.Tramming_Budget_Grade / totalSummary.Tramming_Budget_Tonnage_Sum : 0;
      totalSummary.Tramming_Actual_Grade = totalSummary.Tramming_Actual_Tonnage_Sum > 0 ? totalSummary.Tramming_Actual_Grade / totalSummary.Tramming_Actual_Tonnage_Sum : 0;

      const summaryData = Object.keys(summary).map(key => ({
        [summary_field]: key,
        [planTonnageKey]: summary[key][planTonnageKey],
        Planned_Tonnage: summary[key].Planned_Tonnage,
        Mined_Tonnage: summary[key].Mined_Tonnage,
        Tramming_Budget_Tonnage: summary[key].Tramming_Budget_Tonnage,
        Tramming_Actual_Tonnage: summary[key].Tramming_Actual_Tonnage,
        [planGradeKey]: summary[key][planTonnageSumKey] > 0 ? Number((summary[key][planGradeKey] / summary[key][planTonnageSumKey]).toFixed(2)) : 0,
        Planned_Grade: summary[key].Planned_Tonnage_Sum > 0 ? Number((summary[key].Planned_Grade / summary[key].Planned_Tonnage_Sum).toFixed(2)) : 0,
        Actual_Grade: summary[key].Mined_Tonnage_Sum > 0 ? Number((summary[key].Actual_Grade / summary[key].Mined_Tonnage_Sum).toFixed(2)) : 0,
        Tramming_Budget_Grade: summary[key].Tramming_Budget_Tonnage_Sum > 0 ? Number((summary[key].Tramming_Budget_Grade / summary[key].Tramming_Budget_Tonnage_Sum).toFixed(2)) : 0,
        Tramming_Actual_Grade: summary[key].Tramming_Actual_Tonnage_Sum > 0 ? Number((summary[key].Tramming_Actual_Grade / summary[key].Tramming_Actual_Tonnage_Sum).toFixed(2)) : 0,
        [planContentKey]: summary[key][planContentKey],
        Planned_Content: summary[key].Planned_Content,
        Actual_Content: summary[key].Actual_Content,
        Tramming_Budget_Content: summary[key].Tramming_Budget_Content,
        Tramming_Actual_Content: summary[key].Tramming_Actual_Content
      }));

      setAllData(totalSummary)
      setData(summaryData);

      console.log(summaryData)
    
  }, [stopeID, summary_field, stopeData, parsedData, loading, dataLoading, fiscalYear]);

  if (loading || dataLoading) {
    return (
      <div className="flex items-center justify-center gap-2 p-8">
        <div className="h-5 w-5 rounded-full border border-yellow-500/60 border-t-transparent animate-spin" />
        <span className="text-[11px] text-zinc-400 uppercase tracking-wider">Loading…</span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-transparent z-10 space-y-3">
      {!stopeID && <Card parsedData={parsedData} setStopeData={setStopeData} />}
      <div className="px-0.5">
        <label className="block text-[9px] uppercase tracking-[0.14em] text-yellow-600/80 mb-1">
          Summarize by
        </label>
        <select
          value={summary_field}
          onChange={(e) => setSummaryField(e.target.value)}
          className="w-full rounded-sm border border-yellow-600/30 bg-black/50 px-2.5 py-1.5 text-[11px] text-zinc-200 outline-none focus:border-yellow-500/50"
        >
          <option value="Month">Month</option>
          <option value="Quarter">Quarter</option>
          <option value="Year">Year</option>
        </select>
      </div>

      <div className="rounded-sm border border-yellow-600/25 bg-black/40 overflow-hidden">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-yellow-500/90 px-2.5 py-2 border-b border-yellow-600/20">
          Summary
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[10px]">
            <thead className="bg-zinc-950/80">
              <tr className="text-yellow-600/80">
                <th className="px-2 py-1.5 text-left font-semibold uppercase tracking-wider border-b border-yellow-600/15">
                  Metric
                </th>
                <th className="px-2 py-1.5 text-center font-semibold uppercase tracking-wider border-b border-yellow-600/15">
                  {fiscalYear}
                </th>
                <th className="px-2 py-1.5 text-center font-semibold uppercase tracking-wider border-b border-yellow-600/15">
                  Stoping Bud.
                </th>
                <th className="px-2 py-1.5 text-center font-semibold uppercase tracking-wider border-b border-yellow-600/15">
                  Stoping Act.
                </th>
                <th className="px-2 py-1.5 text-center font-semibold uppercase tracking-wider border-b border-yellow-600/15">
                  Tram Bud.
                </th>
                <th className="px-2 py-1.5 text-center font-semibold uppercase tracking-wider border-b border-yellow-600/15">
                  Tram Act.
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-zinc-900/80 hover:bg-yellow-500/5">
                <td className="px-2 py-1.5 text-zinc-400 font-medium">Tonnage</td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata[`${fiscalYear}_Plan_Tonnage`] || 0).toLocaleString('en-EN', {
                    maximumFractionDigits: 0,
                  })}{' '}
                  t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Planned_Tonnage).toLocaleString('en-EN', { maximumFractionDigits: 0 })} t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Mined_Tonnage).toLocaleString('en-EN', { maximumFractionDigits: 0 })} t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Tramming_Budget_Tonnage).toLocaleString('en-EN', {
                    maximumFractionDigits: 0,
                  })}{' '}
                  t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Tramming_Actual_Tonnage).toLocaleString('en-EN', {
                    maximumFractionDigits: 0,
                  })}{' '}
                  t
                </td>
              </tr>
              <tr className="border-b border-zinc-900/80 hover:bg-yellow-500/5">
                <td className="px-2 py-1.5 text-zinc-400 font-medium">Grade</td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata[`${fiscalYear}_Plan_Grade`] || 0).toFixed(2)} g/t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Planned_Grade).toFixed(2)} g/t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Actual_Grade).toFixed(2)} g/t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Tramming_Budget_Grade).toFixed(2)} g/t
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Tramming_Actual_Grade).toFixed(2)} g/t
                </td>
              </tr>
              <tr className="hover:bg-yellow-500/5">
                <td className="px-2 py-1.5 text-zinc-400 font-medium">Content</td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata[`${fiscalYear}_Plan_Content`] || 0).toLocaleString('en-EN', {
                    maximumFractionDigits: 1,
                  })}{' '}
                  kg
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Planned_Content).toLocaleString('en-EN', { maximumFractionDigits: 1 })} kg
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Actual_Content).toLocaleString('en-EN', { maximumFractionDigits: 1 })} kg
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Tramming_Budget_Content).toLocaleString('en-EN', {
                    maximumFractionDigits: 1,
                  })}{' '}
                  kg
                </td>
                <td className="px-2 py-1.5 text-zinc-200 text-center tabular-nums">
                  {Number(alldata.Tramming_Actual_Content).toLocaleString('en-EN', {
                    maximumFractionDigits: 1,
                  })}{' '}
                  kg
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <GridItem title="Broken tonnes" key={1}>
        <SummaryChart
          stopeID={stopeID}
          summary_field={summary_field}
          data={data}
          target_fields={[
            {
              column_name: `${fiscalYear}_Breaking_Plan_t`,
              legend_name: fiscalYear,
              graphKey: `${fiscalYear}_Plan_Tonnage`,
            },
            { column_name: 'Stoping_Budget_t', legend_name: 'Stoping Budget', graphKey: 'Planned_Tonnage' },
            { column_name: 'Stoping_Actual_t', legend_name: 'Stoping Actual', graphKey: 'Mined_Tonnage' },
            {
              column_name: 'Tramming_Budget_t',
              legend_name: 'Tramming Budget',
              graphKey: 'Tramming_Budget_Tonnage',
            },
            {
              column_name: 'Tramming_Actual_t',
              legend_name: 'Tramming Actual',
              graphKey: 'Tramming_Actual_Tonnage',
            },
          ]}
          graphOptions={{
            graph_type: 'BarChart',
            graph_title: 'Ore Source Reconciliation',
            x_title: 'Month',
            y_title: 'Tonnage (t)',
          }}
        />
      </GridItem>

      <GridItem title="Grade" key={2}>
        <SummaryChart
          stopeID={stopeID}
          summary_field={summary_field}
          data={data}
          target_fields={[
            {
              column_name: `${fiscalYear}_Breaking_Plan_gpt`,
              legend_name: fiscalYear,
              graphKey: `${fiscalYear}_Plan_Grade`,
            },
            { column_name: 'Stoping_Budget_gpt', legend_name: 'Stoping Budget', graphKey: 'Planned_Grade' },
            { column_name: 'Stoping_Actual_gpt', legend_name: 'Stoping Actual', graphKey: 'Actual_Grade' },
            {
              column_name: 'Tramming_Budget_gpt',
              legend_name: 'Tramming Budget',
              graphKey: 'Tramming_Budget_Grade',
            },
            {
              column_name: 'Tramming_Actual_gpt',
              legend_name: 'Tramming Actual',
              graphKey: 'Tramming_Actual_Grade',
            },
          ]}
          graphOptions={{
            graph_type: 'BarChart',
            graph_title: 'Ore Source Reconciliation',
            x_title: 'Month',
            y_title: 'Grade (g/t)',
          }}
        />
      </GridItem>

      <GridItem title="Content" key={3}>
        <SummaryChart
          stopeID={stopeID}
          summary_field={summary_field}
          data={data}
          target_fields={[
            {
              column_name: `${fiscalYear}_Breaking_Plan_kgs`,
              legend_name: fiscalYear,
              graphKey: `${fiscalYear}_Plan_Content`,
            },
            { column_name: 'Stoping_Budget_kgs', legend_name: 'Stoping Budget', graphKey: 'Planned_Content' },
            { column_name: 'Stoping_Actual_kgs', legend_name: 'Stoping Actual', graphKey: 'Actual_Content' },
            {
              column_name: 'Tramming_Budget_kgs',
              legend_name: 'Tramming Budget',
              graphKey: 'Tramming_Budget_Content',
            },
            {
              column_name: 'Tramming_Actual_kgs',
              legend_name: 'Tramming Actual',
              graphKey: 'Tramming_Actual_Content',
            },
          ]}
          graphOptions={{
            graph_type: 'BarChart',
            graph_title: 'Ore Source Reconciliation',
            x_title: 'Month',
            y_title: 'Gold (kg)',
          }}
        />
      </GridItem>
    </div>
  );
};

export default StopeChart;

const SummaryChart = ({ data, summary_field, target_fields, graphOptions }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500 text-[11px]">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={260} className="relative z-20">
      <BarChart
        width={500}
        height={500}
        data={Object.values(data)}
        margin={{
          top: 16,
          right: 8,
          bottom: 16,
          left: 4,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(234,179,8,0.12)" />
        <XAxis
          dataKey={summary_field}
          fontSize={9}
          tick={{ fill: '#a1a1aa' }}
          axisLine={{ stroke: 'rgba(234,179,8,0.25)' }}
          tickLine={{ stroke: 'rgba(234,179,8,0.2)' }}
        />
        <YAxis
          fontSize={9}
          tick={{ fill: '#a1a1aa' }}
          axisLine={{ stroke: 'rgba(234,179,8,0.25)' }}
          tickLine={{ stroke: 'rgba(234,179,8,0.2)' }}
          tickFormatter={
            graphOptions.y_title.includes('Grade')
              ? (v) => v.toFixed(2)
              : (v) => v.toLocaleString()
          }
          label={{
            value: graphOptions.y_title,
            angle: -90,
            position: 'insideLeft',
            fill: '#71717a',
            fontSize: 9,
          }}
        />
        <Tooltip content={CustomTooltip} />
        <Legend wrapperStyle={{ fontSize: 9, color: '#a1a1aa' }} />

        {target_fields.map((field, index) => (
          <Bar key={field.graphKey} dataKey={field.graphKey} fill={colors[index]} name={field.legend_name} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-md border border-yellow-600/40 bg-zinc-950 px-2.5 py-2 shadow-xl">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-yellow-400 mb-1">{`${label}`}</p>
        {payload.map((item, index) => (
          <p className="text-[10px] text-zinc-300" key={index}>
            <span className="text-zinc-500">{item.name}: </span>
            {Number(item.value).toLocaleString('en-EN', {
              maximumFractionDigits: item.name.endsWith('Grade') ? 2 : 2,
            })}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

function GridItem({ title, children }) {
  return (
    <div className="flex flex-col rounded-sm border border-yellow-600/25 bg-black/40 overflow-hidden">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-yellow-500/90 px-2.5 py-2 border-b border-yellow-600/20">
        {title}
      </h3>
      <div className="w-full h-72 px-1 py-2">{children}</div>
    </div>
  );
}

const Card = ({ parsedData, setStopeData }) => {
  const [selectedLevels, setSelectedLevels] = useState(['1L', '2L', '3L', '4L', '5L', '6L']);
  const [period, setPeriod] = useState('Month');
  const [selectedPeriod, setSelectedPeriod] = useState([]);
  const [selectPeriodOptions, setSelectPeriodOptions] = useState([]);

  const levels = ['1L', '2L', '3L', '4L', '5L', '6L'];
  const periods = ['Month', 'Quarter', 'Year'];

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  useEffect(() => {
    setStopeData([selectedLevels, period, selectedPeriod]);
  }, [selectedLevels, period, selectedPeriod, setStopeData]);

  useEffect(() => {
    if (!parsedData) return;
    if (!parsedData.length > 0) return;
    setSelectedPeriod([]);

    const processedData = parsedData.map((row) => {
      const dateStr = row.Date;
      let date = null;

      if (dateStr) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        }
      }

      return {
        ...row,
        Month: date ? date.toLocaleString('default', { month: 'long' }) + ' ' + date.getFullYear() : '',
        Quarter: date ? `Q${Math.floor((date.getMonth() + 3) / 3)} ${date.getFullYear()}` : '',
        Year: date ? date.getFullYear().toString() : '',
      };
    });

    const uniquePeriods = [...new Set(processedData.map((item) => item[period]).filter(Boolean))];

    let sortedPeriods = uniquePeriods;
    if (period === 'Year') {
      sortedPeriods = uniquePeriods.sort((a, b) => parseInt(a) - parseInt(b));
    } else if (period === 'Quarter') {
      sortedPeriods = uniquePeriods.sort((a, b) => {
        const aMatch = a.match(/Q(\d+)\s+(\d+)/);
        const bMatch = b.match(/Q(\d+)\s+(\d+)/);
        if (!aMatch || !bMatch) return a.localeCompare(b);
        const aYear = parseInt(aMatch[2]);
        const bYear = parseInt(bMatch[2]);
        if (aYear !== bYear) return aYear - bYear;
        return parseInt(aMatch[1]) - parseInt(bMatch[1]);
      });
    } else if (period === 'Month') {
      sortedPeriods = uniquePeriods.sort((a, b) => {
        const aDate = new Date(a);
        const bDate = new Date(b);
        if (isNaN(aDate.getTime()) || isNaN(bDate.getTime())) return a.localeCompare(b);
        return aDate.getTime() - bDate.getTime();
      });
    }

    setSelectPeriodOptions(sortedPeriods);
  }, [period, parsedData]);

  if (!parsedData) return null;
  if (!parsedData.length > 0) return null;

  const itemClickedPeriod = (item) => {
    setSelectedPeriod((prevSelected) =>
      prevSelected.includes(item) ? prevSelected.filter((i) => i !== item) : [...prevSelected, item]
    );
  };

  const itemClickedLevel = (item) => {
    setSelectedLevels((prevSelected) =>
      prevSelected.includes(item) ? prevSelected.filter((i) => i !== item) : [...prevSelected, item]
    );
  };

  return (
    <div className="rounded-sm border border-yellow-600/25 bg-black/40 p-2.5 space-y-2">
      <MultiSelectDropdown
        label="Level"
        list={levels}
        selectedlist={selectedLevels}
        itemClick={itemClickedLevel}
      />
      <div>
        <label className="block text-[9px] uppercase tracking-[0.14em] text-yellow-600/80 mb-1">
          Period type
        </label>
        <select
          value={period}
          onChange={handlePeriodChange}
          className="w-full rounded-sm border border-yellow-600/30 bg-black/50 px-2.5 py-1.5 text-[11px] text-zinc-200 outline-none focus:border-yellow-500/50"
        >
          {periods.map((prd) => (
            <option key={prd} value={prd}>
              {prd}
            </option>
          ))}
        </select>
      </div>
      <MultiSelectDropdown
        label="Options"
        list={selectPeriodOptions}
        selectedlist={selectedPeriod}
        itemClick={itemClickedPeriod}
      />
    </div>
  );
};

